import { doc, getDoc, updateDoc, arrayUnion, arrayRemove } from 'firebase/firestore';
import { db, auth } from '../firebase/config';
import { normalizePost, normalizeService } from './postService';

/**
 * Alternar guardado de un post para el usuario
 * @param {string} userId - ID del usuario
 * @param {string} postId - ID del post
 * @returns {Promise<{ success: boolean, saved?: boolean, error?: string }>}
 */
export const toggleSavePost = async (userId, postId) => {
  try {
    if (!auth.currentUser || auth.currentUser.uid !== userId) {
      return { success: false, error: 'Debes iniciar sesión' };
    }
    if (!postId) {
      return { success: false, error: 'ID de post inválido' };
    }

    const userRef = doc(db, 'users', userId);
    const userSnap = await getDoc(userRef);
    if (!userSnap.exists()) {
      return { success: false, error: 'Usuario no encontrado' };
    }

    const savedPosts = Array.isArray(userSnap.data().savedPosts) ? userSnap.data().savedPosts : [];
    const isSaved = savedPosts.includes(postId);

    if (isSaved) {
      await updateDoc(userRef, { savedPosts: arrayRemove(postId) });
      return { success: true, saved: false };
    }
    await updateDoc(userRef, { savedPosts: arrayUnion(postId) });
    return { success: true, saved: true };
  } catch (err) {
    console.error('toggleSavePost error:', err);
    return { success: false, error: err.message || 'Error al guardar el post' };
  }
};

/**
 * Alternar guardado de un servicio para el usuario
 * @param {string} userId - ID del usuario
 * @param {string} serviceId - ID del servicio
 * @returns {Promise<{ success: boolean, saved?: boolean, error?: string }>}
 */
export const toggleSaveService = async (userId, serviceId) => {
  try {
    if (!auth.currentUser || auth.currentUser.uid !== userId) {
      return { success: false, error: 'Debes iniciar sesión' };
    }
    if (!serviceId) {
      return { success: false, error: 'ID de servicio inválido' };
    }

    const userRef = doc(db, 'users', userId);
    const userSnap = await getDoc(userRef);
    if (!userSnap.exists()) {
      return { success: false, error: 'Usuario no encontrado' };
    }

    const savedServices = Array.isArray(userSnap.data().savedServices) ? userSnap.data().savedServices : [];
    const isSaved = savedServices.includes(serviceId);

    if (isSaved) {
      await updateDoc(userRef, { savedServices: arrayRemove(serviceId) });
      return { success: true, saved: false };
    }
    await updateDoc(userRef, { savedServices: arrayUnion(serviceId) });
    return { success: true, saved: true };
  } catch (err) {
    console.error('toggleSaveService error:', err);
    return { success: false, error: err.message || 'Error al guardar el servicio' };
  }
};

/**
 * Obtener posts guardados del usuario; limpia IDs de posts eliminados
 * @param {string} userId - ID del usuario
 * @returns {Promise<{ success: boolean, posts?: Array, removedPostIds?: Array, error?: string }>}
 */
export const getSavedPosts = async (userId) => {
  try {
    if (!userId) {
      return { success: false, error: 'Usuario no especificado' };
    }

    const userRef = doc(db, 'users', userId);
    const userSnap = await getDoc(userRef);
    if (!userSnap.exists()) {
      return { success: true, posts: [], removedPostIds: [] };
    }

    const savedPostIds = Array.isArray(userSnap.data().savedPosts) ? userSnap.data().savedPosts : [];
    const posts = [];
    const removedPostIds = [];

    for (const postId of savedPostIds) {
      const postRef = doc(db, 'posts', postId);
      const postSnap = await getDoc(postRef);
      if (!postSnap.exists()) {
        removedPostIds.push(postId);
        await updateDoc(userRef, { savedPosts: arrayRemove(postId) }).catch(() => {});
        continue;
      }
      posts.push(normalizePost({ id: postSnap.id, ...postSnap.data() }));
    }

    return { success: true, posts, removedPostIds };
  } catch (err) {
    console.error('getSavedPosts error:', err);
    return { success: false, error: err.message || 'Error al cargar posts guardados' };
  }
};

/**
 * Obtener servicios guardados del usuario; limpia IDs de servicios eliminados
 * @param {string} userId - ID del usuario
 * @returns {Promise<{ success: boolean, services?: Array, removedServiceIds?: Array, error?: string }>}
 */
export const getSavedServices = async (userId) => {
  try {
    if (!userId) {
      return { success: false, error: 'Usuario no especificado' };
    }

    const userRef = doc(db, 'users', userId);
    const userSnap = await getDoc(userRef);
    if (!userSnap.exists()) {
      return { success: true, services: [], removedServiceIds: [] };
    }

    const savedServiceIds = Array.isArray(userSnap.data().savedServices) ? userSnap.data().savedServices : [];
    const services = [];
    const removedServiceIds = [];

    for (const serviceId of savedServiceIds) {
      const serviceRef = doc(db, 'services', serviceId);
      const serviceSnap = await getDoc(serviceRef);
      if (!serviceSnap.exists()) {
        removedServiceIds.push(serviceId);
        await updateDoc(userRef, { savedServices: arrayRemove(serviceId) }).catch(() => {});
        continue;
      }
      services.push(normalizeService({ id: serviceSnap.id, ...serviceSnap.data() }));
    }

    return { success: true, services, removedServiceIds };
  } catch (err) {
    console.error('getSavedServices error:', err);
    return { success: false, error: err.message || 'Error al cargar servicios guardados' };
  }
};
